import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Records a **voice message** from the microphone via `MediaRecorder` +
 * `getUserMedia` — the audio is sent as a chat attachment (distinct from
 * `useSpeechToText`, which turns speech into typed text and keeps audio off our
 * servers). Feature-detected: where `MediaRecorder` / `getUserMedia` is missing
 * (older WebViews, some Tauri WKWebView builds), `supported` is false and the
 * composer simply doesn't show the voice button — no broken affordance.
 *
 * Lifecycle, mirroring the dictation hook's discipline: `start()` opens the mic,
 * `stop()` resolves the finished `{ blob, mime, durationMs }`, `cancel()`
 * discards it, and unmount always tears the mic down so it's never left hot.
 *
 * The recorder picks the first MIME the browser supports, but reports the
 * **base** type (no `;codecs=…`): the server's `isSafeMime` rejects a `;`, and
 * the playable-audio set keys on the base type.
 */

/** Recorder MIME candidates, best first. We store the base type (before `;`). */
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
] as const;

function baseMime(mime: string): string {
  return mime.split(";")[0]!.trim().toLowerCase();
}

/** The first supported recorder MIME (base form), or null if none / unsupported.
 * Exported for the dictation fallback recorder (`dictationFallback.ts`), which
 * records off the same browser surface and must pick containers the same way. */
export function pickRecorderMime(): { recorderMime: string; baseMime: string } | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const candidate of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate)) {
        return { recorderMime: candidate, baseMime: baseMime(candidate) };
      }
    } catch {
      // isTypeSupported can throw on some engines — skip the candidate.
    }
  }
  // Some engines lack isTypeSupported but still record a default container.
  return { recorderMime: "", baseMime: "audio/webm" };
}

export interface VoiceRecording {
  readonly blob: Blob;
  /** Base MIME (e.g. "audio/webm") for the upload + playback. */
  readonly mime: string;
  readonly durationMs: number;
}

export interface VoiceRecorder {
  /** Whether mic recording is available in this environment. */
  readonly supported: boolean;
  /** Whether a recording is in progress right now. */
  readonly recording: boolean;
  /** Elapsed recording time in ms (drives the live timer). */
  readonly elapsedMs: number;
  /** Open the mic and start capturing. No-op if already recording / unsupported. */
  start(): Promise<void>;
  /** Stop + resolve the finished recording (null if nothing was captured / cancelled). */
  stop(): Promise<VoiceRecording | null>;
  /** Stop + discard — the bytes are dropped and the mic released. */
  cancel(): void;
}

function detectSupport(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

export function useVoiceRecorder(opts?: { onError?: (code: string) => void }): VoiceRecorder {
  const supportedRef = useRef<boolean | null>(null);
  if (supportedRef.current === null) supportedRef.current = detectSupport();
  const supported = supportedRef.current;

  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);
  const baseMimeRef = useRef("audio/webm");
  const resolveRef = useRef<((rec: VoiceRecording | null) => void) | null>(null);

  const onErrorRef = useRef(opts?.onError);
  onErrorRef.current = opts?.onError;

  const teardown = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    if (!supported || recorderRef.current) return;
    const picked = pickRecorderMime();
    if (!picked) {
      onErrorRef.current?.("unsupported");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err instanceof Error ? err.name : "";
      onErrorRef.current?.(name === "NotAllowedError" ? "not-allowed" : "audio-capture");
      return;
    }

    cancelledRef.current = false;
    chunksRef.current = [];
    baseMimeRef.current = picked.baseMime;
    streamRef.current = stream;

    let recorder: MediaRecorder;
    try {
      recorder = picked.recorderMime
        ? new MediaRecorder(stream, { mimeType: picked.recorderMime })
        : new MediaRecorder(stream);
    } catch {
      // The chosen mimeType wasn't actually accepted — fall back to default.
      recorder = new MediaRecorder(stream);
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
      const chunks = chunksRef.current;
      const resolve = resolveRef.current;
      resolveRef.current = null;
      const cancelled = cancelledRef.current;
      teardown();
      if (cancelled || chunks.length === 0) {
        resolve?.(null);
        return;
      }
      const blob = new Blob(chunks, { type: baseMimeRef.current });
      resolve?.({ blob, mime: baseMimeRef.current, durationMs });
    };

    startedAtRef.current = Date.now();
    setElapsedMs(0);
    try {
      recorder.start();
    } catch {
      onErrorRef.current?.("audio-capture");
      teardown();
      return;
    }
    setRecording(true);
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 200);
  }, [supported, teardown]);

  const stop = useCallback((): Promise<VoiceRecording | null> => {
    const recorder = recorderRef.current;
    if (!recorder) return Promise.resolve(null);
    cancelledRef.current = false;
    return new Promise<VoiceRecording | null>((resolve) => {
      resolveRef.current = resolve;
      try {
        recorder.stop();
      } catch {
        resolveRef.current = null;
        teardown();
        resolve(null);
      }
    });
  }, [teardown]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    cancelledRef.current = true;
    if (recorder) {
      try {
        recorder.stop();
        return; // onstop discards + tears down.
      } catch {
        // fall through to a hard teardown
      }
    }
    teardown();
  }, [teardown]);

  // Never leave the mic hot.
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          // ignore
        }
      }
      teardown();
    };
  }, [teardown]);

  return { supported, recording, elapsedMs, start, stop, cancel };
}
